// Login page entry. Four Cognito-backed flows wired in place - no
// navigation between sub-screens:
//   1. Sign in (with optional Remember-me persistence). If Cognito returns the
//      FORCE_CHANGE_PASSWORD challenge (admin-created accounts), the new-password
//      form is shown instead of the redirect.
//   2. New-password challenge - completes a temp-password sign-in.
//   3. Forgot password (toggled in via "Wachtwoord vergeten?") -
//      Cognito resetPassword + confirmResetPassword.
//   4. Partner-signup (toggled in via the "Meld uw stroompunten aan"
//      link in the aside) - submits to a future applications
//      endpoint; v1 shows an inline acknowledgement.
import './site';
import { configureAmplify } from './lib/amplify';
import {
  cognitoErrorKey,
  completeNewPassword,
  confirmResetPassword,
  resetPassword,
  signIn,
} from './lib/auth';
import { setButtonBusy } from './lib/busy';
import { safeNextUrl } from './lib/redirect';
import { t } from './i18n/i18n';

const loginForm = document.getElementById('login-form') as HTMLFormElement | null;
const newpwdForm = document.getElementById('newpwd-form') as HTMLFormElement | null;
const signupForm = document.getElementById('signup-form') as HTMLFormElement | null;
const forgotForm = document.getElementById('forgot-form') as HTMLFormElement | null;
const loginError = document.getElementById('login-error') as HTMLElement | null;
const newpwdError = document.getElementById('newpwd-error') as HTMLElement | null;
const signupError = document.getElementById('signup-error') as HTMLElement | null;
const signupSuccess = document.getElementById('signup-success') as HTMLElement | null;
const forgotError = document.getElementById('forgot-error') as HTMLElement | null;
const forgotSuccess = document.getElementById('forgot-success') as HTMLElement | null;
const forgotStepEmail = document.getElementById('forgot-step-email');
const forgotStepConfirm = document.getElementById('forgot-step-confirm');

// .login-form__error is `display: none` by default and only renders with
// the .is-shown class - toggling el.hidden is not enough.
function showError(el: HTMLElement | null, msg: string): void {
  if (!el) return;
  el.textContent = msg;
  el.classList.add('is-shown');
}
function clearError(el: HTMLElement | null): void {
  if (!el) return;
  el.classList.remove('is-shown');
  el.textContent = '';
}
// Success boxes have no special CSS - `hidden` works for them.
function showSuccess(el: HTMLElement | null, msg: string): void {
  if (!el) return;
  el.textContent = msg;
  el.hidden = false;
}
function clearSuccess(el: HTMLElement | null): void {
  if (!el) return;
  el.hidden = true;
  el.textContent = '';
}

function submitButtonOf(form: HTMLFormElement): HTMLButtonElement | null {
  return form.querySelector<HTMLButtonElement>('button[type="submit"]');
}

function showLoginError(err: unknown): void { showError(loginError, t(cognitoErrorKey(err))); }
function showNewpwdError(err: unknown): void { showError(newpwdError, t(cognitoErrorKey(err))); }
function showForgotError(err: unknown): void { showError(forgotError, t(cognitoErrorKey(err))); }

type FormId = 'login' | 'signup' | 'forgot' | 'newpwd';
function show(formId: FormId): void {
  if (loginForm) loginForm.hidden = formId !== 'login';
  if (signupForm) signupForm.hidden = formId !== 'signup';
  if (forgotForm) forgotForm.hidden = formId !== 'forgot';
  if (newpwdForm) newpwdForm.hidden = formId !== 'newpwd';
  // Wipe stale messages so a fresh form starts clean.
  clearError(loginError);
  clearError(newpwdError);
  clearError(forgotError);
  clearError(signupError);
  clearSuccess(forgotSuccess);
  clearSuccess(signupSuccess);
}

// "show-*" / "back-to-login*" links live inside i18n-html paragraphs, so
// they only exist after applyTranslations runs. Use event delegation.
document.addEventListener('click', (event) => {
  const target = event.target as HTMLElement | null;
  if (!target) return;
  if (target.closest('#show-signup')) {
    event.preventDefault();
    show('signup');
  } else if (target.closest('#show-forgot')) {
    event.preventDefault();
    if (forgotStepEmail) forgotStepEmail.hidden = false;
    if (forgotStepConfirm) forgotStepConfirm.hidden = true;
    show('forgot');
  } else if (target.closest('#back-to-login') || target.closest('#back-to-login-from-forgot')) {
    event.preventDefault();
    show('login');
  }
});

const params = new URL(window.location.href).searchParams;
// Where to go after sign-in: the admin page that sent the user here
// (?next=, same-origin paths only) or the dashboard.
const postLoginUrl = safeNextUrl(params.get('next'));
if (params.get('error') === 'not-admin') {
  showError(loginError, t('inloggen.errors.not_admin'));
}
if (params.get('signedUp') === '1') {
  const successBox = document.createElement('p');
  successBox.className = 'success-box';
  successBox.textContent = t('inloggen.signedup_success', 'Account created. Sign in below.');
  loginForm?.parentElement?.insertBefore(successBox, loginForm);
}

// Auth-pending gate: the body ships with [data-auth-pending] so the login
// form doesn't flash for users who already have a valid session - those get
// redirected straight on (to ?next= or /admin.html). Only after we've confirmed there's no
// active session do we reveal the form.
//
// Skip the redirect when the URL carries ?error=not-admin: that means the
// user IS signed in but lacks a management role, and admin.html bounced them
// back here to surface the error. Auto-redirecting in that case loops.
void (async () => {
  await configureAmplify();
  const skipRedirect = params.get('error') === 'not-admin';
  if (!skipRedirect) {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        window.location.replace(postLoginUrl);
        return;
      }
    } catch {
      // No active session - proceed to show the form.
    }
  }
  document.body.removeAttribute('data-auth-pending');
})();

// ---- Sign in --------------------------------------------------------------

loginForm?.addEventListener('submit', async (event) => {
  event.preventDefault();
  clearError(loginError);

  const data = new FormData(loginForm);
  const email = String(data.get('email') ?? '').trim();
  const password = String(data.get('password') ?? '');
  const remember = data.get('remember') === 'on';
  if (!email || !password) {
    showError(loginError, t('inloggen.errors.empty_fields'));
    return;
  }

  const loginBtn = submitButtonOf(loginForm);
  setButtonBusy(loginBtn, true);
  try {
    const result = await signIn(email, password, remember);
    if (result.isSignedIn) {
      // Stay busy through the redirect so the button can't be re-clicked
      // while the browser navigates.
      window.location.replace(postLoginUrl);
      return;
    }
    if (result.requiresNewPassword) {
      setButtonBusy(loginBtn, false);
      show('newpwd');
      return;
    }
    if (result.requiresPasswordReset) {
      // Admin force-reset: hand off to the forgot-password flow.
      setButtonBusy(loginBtn, false);
      if (forgotStepEmail) forgotStepEmail.hidden = false;
      if (forgotStepConfirm) forgotStepConfirm.hidden = true;
      const fpEmail = document.getElementById('fp-email') as HTMLInputElement | null;
      if (fpEmail) fpEmail.value = email;
      show('forgot');
      showError(forgotError, t('inloggen.errors.reset_required'));
      return;
    }
    // Unknown next step - surface something rather than silently stalling.
    setButtonBusy(loginBtn, false);
    showError(loginError, t('inloggen.errors.generic'));
  } catch (err) {
    setButtonBusy(loginBtn, false);
    showLoginError(err);
  }
});

// ---- New-password challenge ----------------------------------------------

newpwdForm?.addEventListener('submit', async (event) => {
  event.preventDefault();
  clearError(newpwdError);

  const newPassword = (document.getElementById('np-new-password') as HTMLInputElement | null)?.value ?? '';
  const confirmPassword = (document.getElementById('np-confirm-password') as HTMLInputElement | null)?.value ?? '';
  if (!newPassword || !confirmPassword) {
    showError(newpwdError, t('inloggen.errors.empty_fields'));
    return;
  }
  if (newPassword !== confirmPassword) {
    showError(newpwdError, t('inloggen.newpwd.mismatch'));
    return;
  }

  const newpwdBtn = newpwdForm ? submitButtonOf(newpwdForm) : null;
  setButtonBusy(newpwdBtn, true);
  try {
    await completeNewPassword(newPassword);
    // Stays busy through the redirect.
    window.location.replace(postLoginUrl);
  } catch (err) {
    setButtonBusy(newpwdBtn, false);
    showNewpwdError(err);
  }
});

// ---- Forgot password ------------------------------------------------------

document.getElementById('forgot-send-code')?.addEventListener('click', async () => {
  clearError(forgotError);
  clearSuccess(forgotSuccess);
  const email = (document.getElementById('fp-email') as HTMLInputElement | null)?.value?.trim() ?? '';
  if (!email) {
    showError(forgotError, t('inloggen.errors.empty_fields'));
    return;
  }
  const sendCodeBtn = document.getElementById('forgot-send-code') as HTMLButtonElement | null;
  setButtonBusy(sendCodeBtn, true);
  try {
    await resetPassword(email);
    if (forgotStepEmail) forgotStepEmail.hidden = true;
    if (forgotStepConfirm) forgotStepConfirm.hidden = false;
    showSuccess(forgotSuccess, t('inloggen.forgot.code_sent'));
  } catch (err) {
    showForgotError(err);
  } finally {
    setButtonBusy(sendCodeBtn, false);
  }
});

forgotForm?.addEventListener('submit', async (event) => {
  event.preventDefault();
  clearError(forgotError);

  const email = (document.getElementById('fp-email') as HTMLInputElement | null)?.value?.trim() ?? '';
  const code = (document.getElementById('fp-code') as HTMLInputElement | null)?.value?.trim() ?? '';
  const newPassword = (document.getElementById('fp-new-password') as HTMLInputElement | null)?.value ?? '';
  if (!email || !code || !newPassword) {
    showError(forgotError, t('inloggen.errors.empty_fields'));
    return;
  }

  const forgotBtn = forgotForm ? submitButtonOf(forgotForm) : null;
  setButtonBusy(forgotBtn, true);
  try {
    await confirmResetPassword(email, code, newPassword);
    showSuccess(forgotSuccess, t('inloggen.forgot.success'));
    if (forgotStepConfirm) forgotStepConfirm.hidden = true;
    setTimeout(() => show('login'), 1200);
  } catch (err) {
    showForgotError(err);
  } finally {
    setButtonBusy(forgotBtn, false);
  }
});

// ---- Partner-signup -------------------------------------------------------

signupForm?.addEventListener('submit', async (event) => {
  event.preventDefault();
  clearError(signupError);
  clearSuccess(signupSuccess);

  const data = new FormData(signupForm);
  const payload = {
    organisation: String(data.get('organisation') ?? '').trim(),
    name:         String(data.get('name') ?? '').trim(),
    email:        String(data.get('email') ?? '').trim(),
    phone:        String(data.get('phone') ?? '').trim() || null,
    message:      String(data.get('message') ?? '').trim() || null,
  };
  if (!payload.organisation || !payload.name || !payload.email) {
    showError(signupError, t('inloggen.errors.empty_fields'));
    return;
  }

  const submitBtn = submitButtonOf(signupForm);
  setButtonBusy(submitBtn, true);
  try {
    const { default: axios } = await import('axios');
    await axios.post('/api/applications', payload);
    showSuccess(signupSuccess, t('inloggen.signup.success'));
    signupForm.reset();
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    const msg    = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
    if (status === 409) {
      showError(signupError, msg ?? t('inloggen.signup.err_duplicate'));
    } else if (status === 429) {
      showError(signupError, t('inloggen.errors.rate_limited'));
    } else {
      showError(signupError, msg ?? t('inloggen.signup.err_submit'));
    }
  } finally {
    setButtonBusy(submitBtn, false);
  }
});
